import {css} from 'styled-components';
import utils from 'helpers/utils';

// modifier names come from a fixed vocabulary, so camelcase each one only once
const camelKeys = new Map();

function camelKey (key) {
  let camel = camelKeys.get(key);

  if (!camel) {
    camel = utils.camelcase(key);
    camelKeys.set(key, camel);
  }

  return camel;
}

function overrideFor (overrides, key) {
  return overrides[camelKey(key)] ?? overrides[key];
}

function modifierCss (theme, overrides, variant, selector, key) {
  let styles = '';

  const base = overrideFor(overrides, key);
  if (base) {
    styles += `
      ${selector} {
        ${theme.convert2Css(base)}
      }
    `;
  }

  if (variant) {
    const override = overrideFor(overrides, `${variant}-${key}`);

    if (override) {
      styles += `
        ${selector} {
          ${theme.convert2Css(override)}
        }
      `;
    }
  }

  return styles;
}

/**
 * Kept separate from modifierCss because the behaviour differs and is preserved
 * verbatim: the base branch emits a DESCENDANT selector and returns outright, while
 * the variant branch emits a SELF selector, so the two can never combine. That reads
 * like a bug, but 116 components pass a children modifier and a white-label theme
 * could depend on the current output. Unify it as its own decision.
 */
function childCss (theme, overrides, variant, name, modifier) {
  let styles = '';

  const override = overrideFor(overrides, modifier);
  if (override) {
    return `
      .${name}-${modifier} {
        ${theme.convert2Css(override)}
      }
    `;
  }

  if (variant) {
    const override = overrideFor(overrides, `${variant}-${modifier}`);

    if (override) {
      styles += `
        &.${name}-${modifier} {
          ${theme.convert2Css(override)}
        }
      `;
    }
  }

  return styles;
}

/* General themed component styles */
function componentCss (props) {
  const {theme, variant, $componentName: name, $variant, $modifiers} = props;

  const globalMixin = theme.mixins?.ComponentStyles;
  const globalRoot = theme.components?.ComponentStyles?.styleOverrides?.root;
  const mixin = theme.mixins?.[name];
  const overrides = theme.components?.[name]?.styleOverrides;

  let styles = null;

  // with nothing themed for this component every sweep below yields '', so only
  // walk them when the theme actually has something to say about it
  if (globalMixin || globalRoot || mixin || overrides || $variant?.style) {
    styles = '';

    // default
    if (globalMixin) {
      styles += globalMixin;
    }
    if (globalRoot) {
      styles += theme.convert2Css(globalRoot);
    }

    // overrides
    if (mixin) {
      styles += mixin;
    }
    if (overrides?.root) {
      styles += theme.convert2Css(overrides.root);
    }

    // variants
    if ($variant?.style) {
      styles += theme.convert2Css($variant.style);
    }

    if (overrides) {
      // static modifiers are switched on or off, the style is always there
      ($modifiers?.static ?? []).forEach((modifier) => {
        styles += modifierCss(theme, overrides, variant, `&.${name}-${modifier}`, modifier);
      });

      // variable modifiers change with properties
      ($modifiers?.variable ?? []).forEach((modifier) => {
        if (props[modifier]) {
          const modKey = modifier === 'variant' ? props[modifier] : `${modifier}-${props[modifier]}`;
          styles += modifierCss(theme, overrides, variant, `&.${name}-${modKey}`, modKey);
        }
      });

      // child modifiers hold child overrides, always there
      ($modifiers?.children ?? []).forEach((modifier) => {
        styles += childCss(theme, overrides, variant, name, modifier);
      });
    }
  }

  return styles;
}

const ComponentStyles = css`
  ${(props) => { return componentCss(props); }}
`;

export default ComponentStyles;
